import {useMutation} from '@tanstack/react-query';

import {useDocumentHistoryQueryCache} from '../../../../providers/version/document-history-query';
import {apiClient} from '../../../../utils/api-client';
import {useSearchDocsCache} from '../sidebar/search-layout/hooks/use-edit-mode-search-query-cache';

import {useDocumentQueryCache} from './use-document-query';
import {useLastActionQueryCache} from './use-last-action-query';
import {useOntologyDocsQueryCache} from './use-ontology-docs-query';
import {useUnpublishedDocsQueryCache} from './use-unpublished-docs-query';
import {useFetchWorkingCopyQueryCache} from './use-fetch-working-copy-query';

interface Payload {
    accession: string;
    commitMessage?: string;
}

function publishWorkingCopyRequest(accession: string, commitMessage?: string) {
    return apiClient.post({
        path: `editor-documents-service/api/documents/${accession}/wc/publish`,
        parameters: {commitMessage}
    });
}

/** Publish working copy mutation */
export function usePublishWorkingCopyMutation(belEditorPublished: () => void) {
    const documentQueryCache = useDocumentQueryCache();
    const unpublishedDocsQueryCache = useUnpublishedDocsQueryCache();
    const searchDocsCache = useSearchDocsCache();
    const ontologyDocsQueryCache = useOntologyDocsQueryCache();

    const wcQueryCache = useFetchWorkingCopyQueryCache();

    const lastActionQueryCache = useLastActionQueryCache();
    const documentHistoryQueryCache = useDocumentHistoryQueryCache();

    return useMutation({
        mutationFn: (payload: Payload) =>
            publishWorkingCopyRequest(payload.accession, payload.commitMessage),
        onSuccess: async (_, payload) => {
            lastActionQueryCache.invalidate(payload.accession, 'PUBLISH');
            documentHistoryQueryCache.invalidate(payload.accession);
            unpublishedDocsQueryCache.deleteDocument(payload.accession);

            belEditorPublished();

            const nextDocument = await documentQueryCache.get(payload.accession);

            if (nextDocument) {
                searchDocsCache.updateDocument(nextDocument);
                ontologyDocsQueryCache.updateDocument(nextDocument);
            }

            wcQueryCache.remove(payload.accession);
        }
    });
}
